import React, { useState, useRef } from 'react';
import { importApi, exportApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle,
  Download, ArrowRight, Loader2, FileText, Info, Package, BookOpen, FolderTree
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const ErrorList = ({ errors, maxShow = 5 }) => {
  if (!errors || errors.length === 0) return null;
  const displayErrors = errors.slice(0, maxShow);
  const remaining = errors.length - maxShow;
  
  return (
    <ul className="text-sm list-disc list-inside">
      {displayErrors.map((err, i) => (
        <li key={i}>{err}</li>
      ))}
      {remaining > 0 && <li>... et {remaining} autre(s)</li>}
    </ul>
  );
};

const IMPORT_TYPES = {
  furniture: {
    label: 'Mobilier',
    icon: Package,
    description: 'Importez votre parc mobilier',
    columns: 'code_barres, type_mobilier, marque, modele, localisation, cout_achat, etat',
    template: [
      'code_barres;type_mobilier;marque;modele;localisation;fournisseur;cout_achat;etat;service;collaborateur;notes',
      'MOB-001;bureau;Steelcase;Series 7;Siège Paris > Bâtiment A > Étage 1 > Bureau 101;Bruneau;850;neuf;Direction;Jean Dupont;Bureau direction',
      'MOB-002;chaise;Herman Miller;Aeron;Siège Paris > Bâtiment A;Office Depot;1200;bon;RH;;Chaise ergonomique'
    ]
  },
  catalog: {
    label: 'Catalogue',
    icon: BookOpen,
    description: 'Importez les références du catalogue',
    columns: 'type_mobilier, marque, modele, fournisseur, cout_unitaire, description',
    template: [
      'type_mobilier;marque;modele;fournisseur;cout_unitaire;description;image_url',
      'bureau;Steelcase;Series 7;Bruneau;850;Bureau assis-debout;',
      'chaise;Herman Miller;Aeron;Office Depot;1200;Chaise ergonomique haut de gamme;'
    ]
  },
  locations: {
    label: 'Localisations',
    icon: FolderTree,
    description: 'Importez la hiérarchie des localisations',
    columns: 'type, nom, parent_path, adresse, ville',
    template: [
      'type;nom;parent_path;adresse;ville',
      'site;Siège Paris;;123 Avenue des Champs;Paris',
      'batiment;Bâtiment A;Siège Paris;;',
      'etage;Étage 1;Siège Paris > Bâtiment A;;',
      'bureau;Bureau 101;Siège Paris > Bâtiment A > Étage 1;;'
    ]
  }
};

const ImportPage = () => {
  const { isGestionnaire } = useAuth();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('furniture');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const canImport = isGestionnaire();
  const currentType = IMPORT_TYPES[activeTab];

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Veuillez sélectionner un fichier CSV');
      return;
    }

    setFile(selectedFile);
    setResult(null);
    setError('');
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError('');

    try {
      let response;
      if (activeTab === 'furniture') {
        response = await importApi.execute(file, skipDuplicates);
      } else if (activeTab === 'catalog') {
        response = await importApi.catalog(file, skipDuplicates);
      } else {
        response = await importApi.locations(file);
      }
      
      setResult(response.data);
      setFile(null);
      toast.success(`Import ${currentType.label} terminé`);
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      setError(detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      let response;
      let filename;
      
      if (activeTab === 'furniture') {
        response = await exportApi.furniture();
        filename = 'export_mobilier.csv';
      } else if (activeTab === 'catalog') {
        response = await exportApi.catalog();
        filename = 'export_catalogue.csv';
      } else {
        response = await exportApi.locations();
        filename = 'export_localisations.csv';
      }
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      toast.success('Export téléchargé');
    } catch (err) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = currentType.template.join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `template_${activeTab}.csv`;
    link.click();
  };

  const resetImport = () => {
    setFile(null);
    setResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect({ target: { files: [droppedFile] } });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    resetImport();
  };

  if (!canImport) {
    return (
      <div className="space-y-6 animate-fade-in">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Import / Export</h1>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Vous n&apos;avez pas les droits pour importer des données. Contactez un administrateur.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const resultErrors = result && result.errors ? result.errors : [];
  const IconComponent = currentType.icon;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Import / Export
          </h1>
          <p className="text-slate-500 mt-1">Gérez vos données en masse via fichiers CSV</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="furniture" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Mobilier</span>
          </TabsTrigger>
          <TabsTrigger value="catalog" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Catalogue</span>
          </TabsTrigger>
          <TabsTrigger value="locations" className="flex items-center gap-2">
            <FolderTree className="w-4 h-4" />
            <span className="hidden sm:inline">Localisations</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6 space-y-6">
          {/* Export/Template Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger le modèle
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Exporter les données
            </Button>
          </div>

          {/* Success Result */}
          {result && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-lg text-emerald-900">Import terminé</h3>
                    <p className="text-emerald-700 mt-1">
                      {result.imported} élément(s) importé(s)
                      {result.skipped > 0 && `, ${result.skipped} ignoré(s)`}
                    </p>
                    {resultErrors.length > 0 && (
                      <div className="mt-3 text-sm text-rose-600">
                        <p className="font-medium">Erreurs :</p>
                        <ErrorList errors={resultErrors} maxShow={5} />
                      </div>
                    )}
                    <Button onClick={resetImport} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                      Nouvel import
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upload Area */}
          {!result && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <IconComponent className="w-5 h-5 text-slate-400" />
                  Import {currentType.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    file ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  {file ? (
                    <div className="flex flex-col items-center">
                      <FileText className="w-12 h-12 text-blue-600 mb-4" />
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {(file.size / 1024).toFixed(1)} Ko
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetImport}
                        className="mt-4"
                      >
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-600 mb-2">
                        Glissez-déposez votre fichier CSV ici
                      </p>
                      <p className="text-sm text-slate-400 mb-4">ou</p>
                      <Button
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="select-file-btn"
                      >
                        Sélectionner un fichier
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </div>
                  )}
                </div>

                {/* Format Info */}
                <div className="mt-4 p-4 rounded-lg bg-slate-50">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-slate-600">
                      <p className="font-medium mb-1">{currentType.description}</p>
                      <p>Colonnes : <code className="bg-slate-200 px-1 rounded text-xs">{currentType.columns}</code></p>
                      <p className="mt-1">Séparateur : point-virgule (;) • Encodage : UTF-8</p>
                    </div>
                  </div>
                </div>

                {/* Import Button */}
                {file && (
                  <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                    {activeTab !== 'locations' && (
                      <div className="flex items-center gap-3">
                        <Switch
                          id="skip-duplicates"
                          checked={skipDuplicates}
                          onCheckedChange={setSkipDuplicates}
                        />
                        <Label htmlFor="skip-duplicates" className="text-sm">
                          Ignorer les doublons
                        </Label>
                      </div>
                    )}
                    {activeTab === 'locations' && <div />}

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={resetImport}>
                        Annuler
                      </Button>
                      <Button
                        onClick={handleImport}
                        disabled={importing}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="import-btn"
                      >
                        {importing ? (
                          <span className="flex items-center">
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Import en cours...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            Importer
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImportPage;
